import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon } from 'lucide-react';
import { bookingUrl } from '../data/content';
type PackageId = 'kickstart' | 'launch' | 'expert';
type Package = {
  id: PackageId;
  name: string;
  price: string;
  cadence: string;
  summary: string;
  features: string[];
  featured?: boolean;
};
const packages: Package[] = [{
  id: 'kickstart',
  name: 'Kickstart',
  price: '$$',
  cadence: 'one 60-min session',
  summary: 'A single deep-dive to iron out one main goal. Best fit for someone who has an upcoming interview, presentation, or has general coaching questions.',
  features: ['60-minute 1:1 coaching session', 'Written action plan within 48 hours', '2 weeks of follow-up support within business hours', '10% off Launch package if upgraded']
}, {
  id: 'launch',
  name: 'Launch',
  price: '$$',
  cadence: '2 sessions over 1 month',
  summary: 'Our core program that is time efficient and effective. Best for someone looking to receive support on 2-3 goals.',
  features: ['Two, 60-minute 1:1 coaching sessions', 'Written action plan within 24 hours of each session', 'Async support via Text or Email outside of business hours if needed', '10% off Expert package if upgraded'],
  featured: true
}, {
  id: 'expert',
  name: 'Expert',
  price: '$$$',
  cadence: '4+ sessions over 3 months',
  summary: 'For a longer runway — internship season, senior year, or a first-job pivot with real support.',
  features: ['Everything in Launch', 'Certified LinkedIn badge', 'Peer support group access via WhatsApp', 'Bonus free friend session']
}];
const cardBase = 'flex h-full flex-col rounded-4xl border border-slate-200 bg-pearl-50 p-6 dark:border-slate-800 dark:bg-slate-800 sm:p-7';
const cardFeatured = 'flex h-full flex-col rounded-4xl border border-slate-900 bg-gradient-to-b from-butter-50 to-white p-6 ring-1 ring-slate-900 dark:border-butter-300 dark:from-slate-800 dark:to-slate-900 dark:ring-butter-300 sm:p-7';
const tabBase = 'flex h-full flex-col rounded-3xl border p-4 text-left transition-all duration-200';
const tabOn = 'border-slate-900 bg-gradient-to-b from-butter-50 to-white ring-1 ring-slate-900 dark:border-butter-300 dark:from-slate-800 dark:to-slate-900 dark:ring-butter-300';
const tabOff = 'border-slate-200 bg-pearl-50 dark:border-slate-800 dark:bg-slate-800';
const featureRow = 'flex gap-2.5 text-sm text-slate-700 dark:text-slate-200';
const checkClass = 'mt-0.5 h-4 w-4 flex-none text-butter-600 dark:text-butter-300';
const badgeClass = 'rounded-full bg-gradient-to-r from-butter-300 to-butter-200 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-butter-700 dark:from-butter-400 dark:to-butter-300 dark:text-slate-900';
const buttonSolid = 'mt-6 inline-flex w-full items-center justify-center rounded-full bg-slate-900 px-6 py-3.5 text-sm font-semibold text-pearl-50 transition-colors hover:bg-slate-700 dark:bg-butter-300 dark:text-slate-900 dark:hover:bg-butter-200';
const buttonQuiet = 'mt-6 inline-flex w-full items-center justify-center rounded-full border border-slate-300 bg-white px-6 py-3.5 text-sm font-semibold text-slate-800 transition-colors hover:border-slate-400 dark:border-slate-600 dark:bg-slate-900 dark:text-pearl-100';
export function Pricing() {
  const [selected, setSelected] = useState<PackageId>('launch');
  const active = packages.find(p => p.id === selected) ?? packages[1];
  return <section id="pricing" className="bg-white py-14 dark:bg-slate-900 sm:py-20" data-id="element-177" data-mp="j1eug8dwp7gt">
      <div className="mx-auto max-w-6xl px-5 sm:px-8" data-id="element-178" data-mp="nywt95y7m6b3">
        <div className="max-w-2xl" data-id="element-179" data-mp="a8jm35t56vmt">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-plum-500 dark:text-plum-300" data-id="element-180" data-mp="a7i9gwwha12z">
            Pricing
          </p>
          <h2 className="mt-3 font-display text-3xl font-semibold tracking-tight text-slate-900 dark:text-pearl-50 sm:text-4xl" data-id="element-181" data-mp="e823hrlr8okw">
            Three ways to work together
          </h2>
          <p className="mt-4 text-base text-slate-600 dark:text-slate-300 sm:text-lg" data-id="element-182" data-mp="16rqxp5y7ig12">
            Final pricing is shared on the free consult call and in writing before you commit. Pause
            or cancel anytime, and unused sessions are refunded.
          </p>
        </div>

        {/* Desktop: three full cards side by side */}
        <ul className="mt-10 hidden items-start gap-6 lg:grid lg:grid-cols-3" data-id="element-183" data-mp="317r1vtryfs3p">
          {packages.map((pkg, i) => <motion.li key={pkg.id} initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.45,
          delay: i * 0.07,
          ease: [0.22, 1, 0.36, 1]
        }} className={pkg.featured ? cardFeatured : cardBase} data-id="element-184" data-mp="1scgf60e3a3c" data-mpi={i}>
              <div className="flex items-center justify-between gap-3" data-id="element-185" data-mp="kiv9s6syjf2b">
                <h3 className="font-display text-xl font-semibold text-slate-900 dark:text-pearl-50" data-id="element-186" data-mp="kl829dwcd7tm">
                  {pkg.name}
                </h3>
                {pkg.featured && <span className={badgeClass} data-id="element-187" data-mp="1yggcuzn3yy1q">CLIENT FAVORITE</span>}
              </div>

              <p className="mt-5 flex items-baseline gap-2" data-id="element-188" data-mp="23f9fty4rr6nn">
                <span className="font-display text-4xl font-semibold text-slate-900 dark:text-pearl-50" data-id="element-189" data-mp="27ftex78wkkcf">
                  {pkg.price}
                </span>
                <span className="text-sm text-slate-500 dark:text-slate-400" data-id="element-190" data-mp="2cgheihb769i9">{pkg.cadence}</span>
              </p>

              <p className="mt-4 leading-relaxed text-slate-600 dark:text-slate-300" data-id="element-191" data-mp="2hbwrl71ds1dm">
                {pkg.summary}
              </p>

              <ul className="mt-6 flex-1 space-y-2.5" data-id="element-192" data-mp="2r42e418v5qlf">
                {pkg.features.map(feature => <li key={feature} className={featureRow} data-id="element-193" data-mp="2v5e8sl6lh20h">
                    <CheckIcon className={checkClass} aria-hidden="true" data-id="element-194" data-mp="2v4w7ha52rxlp" />
                    <span data-id="element-195" data-mp="305juzlu3hwb6">{feature}</span>
                  </li>)}
              </ul>

              <a href={bookingUrl} target="_blank" rel="noopener noreferrer" className={pkg.featured ? buttonSolid : buttonQuiet} data-id="element-196" data-mp="35326njzhypmv">
                Start with {pkg.name}
              </a>
            </motion.li>)}
        </ul>

        {/* Mobile & tablet: compact selector with one expanded panel */}
        <div className="lg:hidden" data-id="element-197" data-mp="1ernjp2xmtqqf">
          <div role="tablist" aria-label="Coaching packages" className="mt-8 grid grid-cols-3 gap-2.5 sm:gap-4" data-id="element-198" data-mp="1esq8n5be9j3p">
            {packages.map(pkg => <button key={pkg.id} type="button" role="tab" aria-selected={selected === pkg.id} aria-controls="package-panel" onClick={() => setSelected(pkg.id)} className={`${tabBase} ${selected === pkg.id ? tabOn : tabOff}`} data-id="element-199" data-mp="19ym2updv7h47">
                <span className="font-display text-base font-semibold text-slate-900 dark:text-pearl-50 sm:text-xl" data-id="element-200" data-mp="1xqj8fyqq1cwv">
                  {pkg.name}
                </span>
                <span className="mt-2 font-display text-2xl font-semibold text-slate-900 dark:text-pearl-50 sm:text-3xl" data-id="element-201" data-mp="1sp3mm4gpz2df">
                  {pkg.price}
                </span>
                <span className="mt-1 text-[11px] leading-snug text-slate-500 dark:text-slate-400 sm:text-sm" data-id="element-202" data-mp="3uk4a4xmlgc9f">
                  {pkg.cadence}
                </span>
                {pkg.featured && <span className="mt-2 self-start rounded-full bg-gradient-to-r from-butter-300 to-butter-200 px-2.5 py-1 text-[9px] font-semibold uppercase tracking-wide text-butter-700 dark:from-butter-400 dark:to-butter-300 dark:text-slate-900 sm:text-[11px]" data-id="element-203" data-mp="2ey0s8kijv9jc">
                    CLIENT FAVORITE
                  </span>}
              </button>)}
          </div>

          <div id="package-panel" role="tabpanel" data-id="element-204" data-mp="1w9dmdzsjug4p">
            <AnimatePresence mode="wait" initial={false} data-id="element-205" data-mp="1wa5kbh9mcpy5">
              <motion.div key={active.id} initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -6
            }} transition={{
              duration: 0.25,
              ease: [0.22, 1, 0.36, 1]
            }} className="mt-4 rounded-4xl border border-slate-200 bg-pearl-50 p-6 dark:border-slate-800 dark:bg-slate-800 sm:p-8" data-id="element-206" data-mp="1hcd5krbwc5l0">
                <p className="max-w-2xl leading-relaxed text-slate-600 dark:text-slate-300" data-id="element-207" data-mp="1zg9651p2xwkp">
                  {active.summary}
                </p>

                <ul className="mt-5 grid gap-2.5 sm:grid-cols-2 sm:gap-x-8" data-id="element-208" data-mp="1uo7y5ryy2s0h">
                  {active.features.map(feature => <li key={feature} className={featureRow} data-id="element-209" data-mp="1ppwn5dwwybgo">
                      <CheckIcon className={checkClass} aria-hidden="true" data-id="element-210" data-mp="1krlfn9u3cd1m" />
                      <span data-id="element-211" data-mp="1gprkfh2o018u">{feature}</span>
                    </li>)}
                </ul>

                <a href={bookingUrl} target="_blank" rel="noopener noreferrer" className={`${buttonSolid} sm:w-auto sm:px-8`} data-id="element-212" data-mp="1gs45w47mstqn">
                  Start with {active.name}
                </a>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>;
}